import { getIpInfo } from "@/apis/ipInfo";
import { IpInfoResponse } from "@/helpers/interfaces/shared";
import { signOut } from "aws-amplify/auth";
import { useSessionStore, useEMandatesStore } from "@/plugins/store";
import { redirectToRoute } from "@/plugins/router";
import { routeNames } from "@/constants/routeNames";
import { supportedLocales } from "@/constants/locale";
import { useOrganisationStore } from "@/plugins/store";

export const isMobileDevice = (): boolean => {
    // Prefer Client Hints when available (Chromium browsers)
    const uaData = (navigator as any).userAgentData;
    if (uaData?.mobile !== undefined) {
        return uaData.mobile;
    }

    // Fallback: UA string check, including modern iPad detection
    const ua = navigator.userAgent;
    const isIOS =
        /iPhone|iPod/i.test(ua) ||
        (/iPad|Macintosh/i.test(ua) && navigator.maxTouchPoints > 1); // catches iPadOS 13+
    const isOtherMobile = /Android|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return isIOS || isOtherMobile;
};

export const getUserIpAddress = async () => {
    /**
     * Calls ipinfo.io to retrieve the user's IP address.
     * Uses the ipinfo.io API, which is limited to 50,000 requests per month in the free tier.
     */
    let userIpAddress = "";
    const data: IpInfoResponse = await getIpInfo();

    if (data !== null) {
        userIpAddress = "ip" in data ? (data.ip as string) : "";
    }

    return userIpAddress;
};

export const handleLogout = async () => {
    const eMandatesStore = useEMandatesStore();
    const sessionStore = useSessionStore();
    const orgStore = useOrganisationStore();

    await signOut();
    sessionStore.setIsAuthenticated(false);
    sessionStore.setUser(null);

    if (!orgStore.shortcode) {
        redirectToRoute(routeNames.DEFAULT, {});
        return;
    }

    if (sessionStore.origin === routeNames.E_MANDATES_LANDING_PAGE) {
        redirectToRoute(routeNames.E_MANDATES_LANDING_PAGE, {
            shortcode: orgStore.shortcode,
            accountAlias: eMandatesStore.accountAlias,
        });
    } else {
        const formName = orgStore.formName ?? "invoice";
        redirectToRoute(routeNames.FORM, {
            shortcode: orgStore.shortcode,
            form: formName,
        });
    }
};

export const getBrowserInformation = async () => {
    /**
     * Collect browser information for fraud prevention purposes.
     * This information is sent to the payment gateway during the payment process.
     * Returns an object containing browser information.
     */
    const browserIp = await getUserIpAddress();
    return {
        browserIp: browserIp,
        browserLanguage: navigator.language || (navigator as any).userLanguage,
        browserColorDepth: window.screen.colorDepth.toString(),
        browserScreenHeight: window.screen.height.toString(),
        browserScreenWidth: window.screen.width.toString(),
        browserTimeZone: new Date().getTimezoneOffset().toString(),
        browserUserAgent: navigator.userAgent,
    };
};

export const getUserLocaleForAdyen = (): string => {
    /**
     * Returns the locale to use for Adyen based on the user's language setting.
     * If the user's language is in the list of supported locales and is not "en-GB",
     * the user's locale is returned. Otherwise, "en-US" is returned as Adyen's
     * default English locale.
     * @returns {string}
     */
    const sessionStore = useSessionStore();
    const userLocale = sessionStore.language;

    if (
        userLocale &&
        Object.keys(supportedLocales).includes(userLocale) &&
        userLocale !== "en-GB"
    ) {
        return userLocale;
    }

    return "en-US";
};
